import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Loader2, CheckCircle2 } from "lucide-react";
import { sendPasswordResetEmail, formatApiError } from "../lib/api";
import SEO from "../seo/SEO";

export const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const redirectTo = `${window.location.origin}/admin/update-password`;
      await sendPasswordResetEmail(email, redirectTo);
      setSent(true);
    } catch (err) {
      setError(formatApiError(err) || "Could not send reset email");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-cream px-5">
      <SEO title="Reset password — Karen Boley Counselling" noindex />
      <div className="w-full max-w-sm bg-white rounded-2xl border border-cream-2 shadow-xl p-8">
        <h1 className="font-serif text-3xl text-ink">Reset your password</h1>
        <p className="text-sm text-ink-muted mt-1">We&apos;ll email you a link to set a new one.</p>

        {sent ? (
          <div data-testid="forgot-success" className="mt-6 rounded-xl bg-sage/10 border border-sage/30 p-4 flex items-start gap-3">
            <CheckCircle2 className="h-5 w-5 text-sage-dark shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-ink">Check your inbox</p>
              <p className="text-sm text-ink-soft mt-1">
                If an account exists for <span className="font-medium">{email}</span>, we&apos;ve sent a link to reset your password. The link expires in 1 hour.
              </p>
            </div>
          </div>
        ) : (
          <form data-testid="forgot-form" onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <label className="block text-xs uppercase tracking-wide text-ink-muted mb-1">Email</label>
              <input
                data-testid="forgot-email"
                type="email"
                required
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-lg border border-cream-2 px-3 py-2.5 text-ink outline-none focus:border-sage"
              />
            </div>
            {error && <p data-testid="forgot-error" className="text-sm text-red-600">{error}</p>}
            <button
              data-testid="forgot-submit"
              type="submit"
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-sage text-white py-3 font-semibold hover:bg-sage-dark transition-colors disabled:opacity-60"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />} Send reset link
            </button>
          </form>
        )}

        <Link
          to="/admin/login"
          data-testid="forgot-back-link"
          className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-sage-dark hover:text-sage transition-colors"
        >
          <ArrowLeft className="h-4 w-4" /> Back to sign in
        </Link>
      </div>
    </div>
  );
};

export default ForgotPassword;
